import React from 'react';

import api            from '../../../../api';
import values         from '../../../../values';
import Context        from '../../../../context';
import successHandler from '../../../../utils/successHandler';


import { useQuery       } from 'react-query';
import { useTranslation } from 'react-i18next';

import { IOnSearchUser     } from '../../../../types/common.interface';
import { locationNamespace } from '../../../../types/location.namespace';

import {
  CustomModal,
  CustomButton2,
  CustomDropdownMultiSearch,
  ResponsiveView,
  LocationDropdowns,
} from '../../../../components';

import './index.sass';

const Deposit: React.FC = (): React.ReactElement => {

  // # =================================================================================================================
  // # --------------------------------------------- Start Validate Values ---------------------------------------------

  const { t } = useTranslation();
  const {showModal, setIsLoading, setShowModal, isLoading, selectedDriver, setSelectedDriver, refetch} = React.useContext(Context.DriverScreen);

  const [selectedNeighborhoods, setSelectedNeighborhoods] = React.useState<Array<locationNamespace.neighborhood.index>>([]);
  const [selectedSchools      , setSelectedSchools      ] = React.useState<Array<any                                 >>([]);

  const [selectedProvince, setSelectedProvince] = React.useState<locationNamespace.province.index | undefined>(undefined);
  const [selectedCity    , setSelectedCity    ] = React.useState<locationNamespace.city    .index | undefined>(undefined);
  const [selectedArea    , setSelectedArea    ] = React.useState<locationNamespace.area    .index | undefined>(undefined);

  const [searchedValueSchool, setSearchedValueSchool] = React.useState<IOnSearchUser>();

  // # ---------------------------------------------- End Validate Values ----------------------------------------------
  // # =================================================================================================================

  // ! =================================================================================================================
  // ! ------------------------------------------------ Start Call API -------------------------------------------------

  const getAllSchool = useQuery(
    [values.useQueryKeys.getAllSchool, searchedValueSchool?.searchedValue],
    () => api.post.common.findSchoolPost({
        search: {
          base : searchedValueSchool?.base?.value,
          value: searchedValueSchool?.searchedValue,
        },
      })
  );

  async function udpateFavorite(): Promise<void> {
    setIsLoading!(true);
    const result = await api.put.common.updateDriverByAdmin({
      _id: selectedDriver?._id,
      favorite: {
        neighborhood: selectedNeighborhoods?.map((item) => item._id),
        school      : selectedSchools      ?.map((item) => item._id),
      }
    });
    if (result.CODE === 2012) {
      successHandler(2012);
      setSelectedSchools([]);
      setSelectedNeighborhoods([]);
      await refetch!();
      setShowModal!('NONE');
    }
    setSelectedDriver!(undefined)
    setIsLoading!(false);
  }

  // ! ------------------------------------------------- End Call API --------------------------------------------------
  // ! =================================================================================================================

  // * =================================================================================================================
  // * -------------------------------------------- Start Local Functions ----------------------------------------------

  function onSelectProvince(province: locationNamespace.province.index): void {
    if (selectedProvince !== province) {
      setSelectedProvince(province);
      setSelectedCity(undefined);
      setSelectedArea(undefined);
    }
  }

  function onSelectCity(city: locationNamespace.city.index): void {
    if (selectedCity !== city) {
      setSelectedCity(city);
      setSelectedArea(undefined);
    }
  }

  function onSelectArea(area: locationNamespace.area.index): void {
    if (selectedArea !== area) {
      setSelectedArea(area);
    }
  }

  function onSelectNeighborhood(neighborhood: locationNamespace.neighborhood.index): void {
    setSelectedNeighborhoods((prevItems) => {
      if (!prevItems) return [neighborhood]
      const exists = prevItems?.some((item) => item._id === neighborhood._id);
      if (exists) return prevItems?.filter((item) => item._id !== neighborhood._id);
      else return [...prevItems, neighborhood];
    });
  }

  function onSelectSchool(school: any): void {
    setSelectedSchools((prevItems) => {
      if (!prevItems) return [school]
      const exists = prevItems.some((item) => item._id === school._id);
      if (exists) return prevItems.filter((item) => item._id !== school._id);
      else return [...prevItems, school];
    });
  }

  function onCloseModal(): void {
    setShowModal!('NONE');
    setSelectedNeighborhoods([]);
    setSelectedSchools([]);
    setSelectedDriver!(undefined);
  }

  // * --------------------------------------------- End Local Functions -----------------------------------------------
  // * =================================================================================================================

  // ! =================================================================================================================
  // ! ------------------------------------------ Start UseEffect functions --------------------------------------------

  React.useEffect(() => {
    setSelectedNeighborhoods(selectedDriver?.schoolDriverInformation?.favorite?.neighborhood);
    setSelectedSchools      (selectedDriver?.schoolDriverInformation?.favorite?.school      );

    setSelectedProvince(selectedDriver?.schoolDriverInformation?.province);
    setSelectedCity    (selectedDriver?.schoolDriverInformation?.city    );
  }, [selectedDriver]);

  // ! ------------------------------------------- End UseEffect functions ---------------------------------------------
  // ! =================================================================================================================

  // ? =================================================================================================================
  // ? ----------------------------------------------- Start Return View -----------------------------------------------

  return (
    <section id="driver-approve-modal-container">
      <CustomModal
        onClose={onCloseModal}
        open={showModal === "FAVORITE"}
        label="علاقه‌مندی‌های راننده"
        minWidth={'60vw'}
        minHeight={'40vh'}
      >
        <ResponsiveView.Container>
          <br/>
          <ResponsiveView.Row>
            <ResponsiveView.Column medium={6}>
              <LocationDropdowns.Province
                defaultItem={selectedProvince}
                onSelectItem={onSelectProvince}
              />
              <LocationDropdowns.Area
                defaultItem={selectedArea}
                onSelectItem={onSelectArea}
                selectedProvince={selectedProvince}
                selectedCity={selectedCity}
              />
            </ResponsiveView.Column>
            <ResponsiveView.Column medium={6}>
              <LocationDropdowns.City
                defaultItem={selectedCity}
                onSelectItem={onSelectCity}
                selectedProvince={selectedProvince}
              />
              <LocationDropdowns.Neighborhood
                defaultItem={undefined}
                onSelectItem={onSelectNeighborhood}
                selectedProvince={selectedProvince}
                selectedCity={selectedCity}
                selectedArea={selectedArea}
              />
            </ResponsiveView.Column>
            <div className="city-items" style={{ flexWrap: 'wrap'}}>
              {selectedNeighborhoods?.map((item: locationNamespace.neighborhood.index, index: number) => (
                <span key={`selected-naighborhood-index-${index}`}>
                {item?.name}{" "}
                  <i
                    onClick={() => {
                      setSelectedNeighborhoods(selectedNeighborhoods?.filter((filterItem) => filterItem._id !== item._id));
                    }}
                    className="fa-solid fa-xmark close-mark"
                  ></i>
              </span>
              ))}
            </div>
          </ResponsiveView.Row>
          <br/>
          <hr/>
          <ResponsiveView.Row>
            <ResponsiveView.Column medium={6}>
              <label className="form-label">{t("school")}</label>
              <CustomDropdownMultiSearch
                isSearch
                baseSearch={values.dropdownBaseSearch.SCHOOL_BASE_SEARCH.map((item) => ({
                  ...item,
                  title: t(item?.title)
                }))}
                items={getAllSchool?.data?.result}
                itemName="name"
                onSelectItem={onSelectSchool}
                defaultItem={undefined}
                searchedValue={searchedValueSchool}
                setSearchedValue={setSearchedValueSchool}
                placeHolder={t("selectSchool")}
                isLoading={getAllSchool.isLoading}
                selectedBaseSearch={searchedValueSchool?.base?.value}
              />
            </ResponsiveView.Column>
            <ResponsiveView.Column medium={12}>
              <div className="city-items" style={{flexWrap: 'wrap'}}>
                {selectedSchools?.map((item: locationNamespace.neighborhood.index, index: number) => (
                  <span key={`selected-naighborhood-index-${index}`}>
                    {item?.name}
                    <i
                      onClick={() => {
                        setSelectedSchools(selectedSchools?.filter((filterItem) => filterItem._id !== item._id));
                      }}
                      className="fa-solid fa-xmark close-mark"
                    />
                  </span>
                ))}
              </div>
            </ResponsiveView.Column>
          </ResponsiveView.Row>
          <br />
          <ResponsiveView.Row>
            <ResponsiveView.Column medium={6}>
              <CustomButton2 title='لغو' type='danger' onClick={onCloseModal} isLoading={isLoading} />
            </ResponsiveView.Column>
            <ResponsiveView.Column medium={6}>
              <CustomButton2 title='تایید' type='primary' onClick={udpateFavorite} isLoading={isLoading} />
            </ResponsiveView.Column>
          </ResponsiveView.Row>
        </ResponsiveView.Container>
      </CustomModal>
    </section>
  );

  // ? ------------------------------------------------ End Return View ------------------------------------------------
  // ? =================================================================================================================

};

export default Deposit;
